import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import PopOverContent from './components/PopoverContent';

interface CustomPopoverInterface {
  clickableElement: JSX.Element | string;
  popOverContent: JSX.Element | string;
  inBetweenSpace?: number;
}

/**
 * Pop-over component - opens a popup near a target element
 * Clickable elements parent component should have a defined width
 * @param {CustomPopoverInterface} properties - CustomPopover properties
 * @returns {React.ReactElement} Pop-over component
 */
function CustomPopover(properties: CustomPopoverInterface) {
  const { clickableElement, popOverContent, inBetweenSpace } = properties;
  const { i18n } = useTranslation('sharedComponent');
  const direction = i18n.dir();

  const popoverReference = useRef<HTMLDivElement | null>(null);
  const buttonReference = useRef<HTMLDivElement | null>(null);
  const [openPopover, setOpenPopover] = useState<boolean>(false);

  /**
   * Function to open a popover near a clickable element
   */
  const handleOpenPopover = () => {
    if (!openPopover && buttonReference.current) {
      setOpenPopover(true);
    }
  };

  /**
   * Function to determine the position of the popover
   */
  const handlePopOverPosition = () => {
    if (!popoverReference.current || !buttonReference.current) {
      return;
    }

    const buttonRect = buttonReference.current.getBoundingClientRect();
    const popoverRect = popoverReference.current.getBoundingClientRect();
    const popoverWidth = popoverRect.width;
    const gap = inBetweenSpace || 3;

    const top = buttonRect.top + buttonRect.height + gap;
    let left = buttonRect.left + buttonRect.width / 2 - popoverWidth / 2;

    const sidebar = document.querySelector('.sidebar-container');
    const sidebarWidth = sidebar ? sidebar.getBoundingClientRect().width : 320;
    const gapOffset = 12;

    if (direction === 'rtl') {
      const rightSafeZone = sidebarWidth + gapOffset;
      const leftMargin = 8;

      // prevent going into right safe zone
      if (left + popoverWidth > window.innerWidth - rightSafeZone) {
        left = window.innerWidth - popoverWidth - rightSafeZone;
      }

      // prevent going off the left edge
      if (left < leftMargin) {
        left = leftMargin;
      }
    } else {
      const leftSafeZone = sidebarWidth + gapOffset;
      const rightMargin = 8;

      // --- prevent going into the left safe zone
      if (left < leftSafeZone) {
        left = leftSafeZone;
      }

      // --- prevent going off the right edge
      if (left + popoverWidth > window.innerWidth - rightMargin) {
        left = window.innerWidth - popoverWidth - rightMargin;
      }
    }

    popoverReference.current.style.top = `${top}px`;
    popoverReference.current.style.left = `${left}px`;
    popoverReference.current.style.position = 'fixed';
    popoverReference.current.style.zIndex = '9999';
  };

  useEffect(() => {
    if (openPopover) {
      handlePopOverPosition();

      // Add scroll and resize listeners to keep popover attached to parent element responsively
      window.addEventListener('resize', handlePopOverPosition);
      window.addEventListener('scroll', handlePopOverPosition, true);

      // Add ResizeObserver to track sidebar width changes
      let resizeObserver: ResizeObserver | null = null;
      const sidebarElement = document.querySelector('.sidebar-container');
      if (sidebarElement) {
        resizeObserver = new ResizeObserver(() => {
          handlePopOverPosition();
        });
        resizeObserver.observe(sidebarElement);
      }

      return () => {
        window.removeEventListener('resize', handlePopOverPosition);
        window.removeEventListener('scroll', handlePopOverPosition, true);
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
      };
    }
    return undefined;
  }, [openPopover]);

  return (
    <div className="custom-popover-wrap">
      <div
        className="pop-over-element"
        role="presentation"
        ref={buttonReference}
        onClick={handleOpenPopover}>
        {clickableElement}
      </div>
      {openPopover ? (
        <PopOverContent
          openPopover={openPopover}
          popOverContent={React.cloneElement(popOverContent as JSX.Element, {
            /**
             * event handler for closing the popover
             * @returns void
             */
            onClose: () => setOpenPopover(false), // ✅ inject onClose
          })}
          ref={popoverReference}
          setOpenPopover={setOpenPopover}
        />
      ) : null}
    </div>
  );
}

export default CustomPopover;
